import React, { useState } from "react";
import TroubleShootingForCode from "./components/TroubleShootingForCode";
import {
  Alert,
  Button,
  Divider,
  Flex,
  Form,
  Input,
  Space,
  Typography,
  message,
} from "antd";
import { INewAppointmentArgs } from ".";
import {
  IPathState,
  RESCHEDULE_LIST,
  RESCHEDULE_MAIN,
  SETUP_PAYMENT,
} from ".";
import type { AppointmentForm } from "@/lib/hooks/useAppointment";
import { extractErrorMessage } from "@/lib/helpers";
import { BOOKING_BLOCKED_MESSAGE } from "@/lib/api/openingsLocations";
import Text from "antd/lib/typography/Text";
import { useBookingApi } from "./bookingApi";

const VERIFICATION_RESEND_COOLDOWN_SECONDS = 45;

interface IProps {
  appointment: AppointmentForm;
  routeState: any;
  onCreate: (args: INewAppointmentArgs) => void;
  onRouteChange: (path: IPathState, state: any) => void;
}
const FirstClient = ({
  appointment,
  routeState,
  onCreate,
  onRouteChange,
}: IProps) => {
  const bookingApi = useBookingApi();
  const [messageApi, contextHolder] = message.useMessage();

  const { phoneNumber, isCreditCardRequired } = routeState;
  const isVerifyingUpcomingAppointments = Boolean(
    routeState.verifyForUpcomingAppointments,
  );
  const verifyDuringAppointmentCreate = Boolean(
    routeState.verifyDuringAppointmentCreate,
  );
  const [firstName, setFirstName] = useState<string>(routeState.firstName);
  const [lastName, setLastName] = useState<string>(routeState.lastName);
  const [code, setCode] = useState<string>(routeState.verificationCode);
  const [isSubmitting, setSubmitting] = useState(false);
  const [isSending, setSending] = useState(false);
  const [resendAvailableAt, setResendAvailableAt] = useState<number>(
    routeState.verificationResendAvailableAt ??
      Date.now() + VERIFICATION_RESEND_COOLDOWN_SECONDS * 1000,
  );
  const [now, setNow] = useState(Date.now());
  const [inlineError, setInlineError] = React.useState<string | null>(null);
  const [showInstructure, setShowInstructure] = React.useState(false);
  const resendCooldownSeconds = Math.max(
    0,
    Math.ceil((resendAvailableAt - now) / 1000),
  );
  // const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (resendCooldownSeconds <= 0) return;

    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [resendCooldownSeconds]);

  const handleSubmit = async () => {
    const verificationCode = code?.trim();

    setInlineError(null);

    if (
      phoneNumber &&
      verificationCode &&
      (isVerifyingUpcomingAppointments || (firstName && lastName))
    ) {
      setSubmitting(true);

      if (isVerifyingUpcomingAppointments) {
        try {
          // Legacy locations return the upcoming list inline at lookup, so the
          // verified-history endpoint is not available. Verify the code and
          // reveal the already-loaded list. Openings-backed locations hide the
          // list until verification, so fetch it via the verified endpoint.
          if (routeState.listAlreadyLoaded) {
            const isVerified = await bookingApi.verifyVerificationCode(
              phoneNumber,
              verificationCode,
              appointment,
            );
            if (!isVerified) {
              setInlineError("Invalid verification code");
              setSubmitting(false);
              return;
            }
            onRouteChange(
              routeState.returnToRescheduleList
                ? RESCHEDULE_LIST
                : RESCHEDULE_MAIN,
              {
                ...routeState,
                verificationCode,
              },
            );
            return;
          }

          const history = await bookingApi.getVerifiedAppointmentHistory(
            phoneNumber,
            verificationCode,
            appointment,
          );
          if (history.client?.bookingBlocked) {
            setInlineError(BOOKING_BLOCKED_MESSAGE);
            setSubmitting(false);
            return;
          }
          onRouteChange(
            routeState.returnToRescheduleList
              ? RESCHEDULE_LIST
              : RESCHEDULE_MAIN,
            {
              phoneNumber,
              upcomingAppointments: history.upcomingAppointments,
              client: history.client ?? routeState.client,
              isCreditCardRequired,
              verificationCode,
            },
          );
        } catch (error) {
          setInlineError(extractErrorMessage(error));
          setSubmitting(false);
        }
        return;
      }

      if (!firstName?.trim() || !lastName?.trim()) {
        setSubmitting(false);
        return;
      }

      let isVerified = verifyDuringAppointmentCreate;
      if (!verifyDuringAppointmentCreate) {
        try {
          isVerified = await bookingApi.verifyVerificationCode(
            phoneNumber,
            verificationCode,
            appointment,
          );
        } catch (error) {
          setInlineError(extractErrorMessage(error));
          setSubmitting(false);
          return;
        }
      }

      if (isVerified) {
        const existingPaymentMethodId = routeState.paymentMethodId;
        if (isCreditCardRequired && !existingPaymentMethodId) {
          // Hand off to the card screen, which shows what will actually be
          // charged. The name must travel with it: the CRM customer is created
          // from whatever the card step sends, and that name can never be
          // changed afterwards.
          onRouteChange(SETUP_PAYMENT, {
            phoneNumber,
            firstName,
            lastName,
            verificationCode,
            isCreditCardRequired,
            verifyDuringAppointmentCreate,
          });
          setSubmitting(false);
          return;
        }

        onCreate({
          firstName,
          lastName,
          phoneNumber,
          code: verificationCode,
          ...(existingPaymentMethodId && {
            paymentMethodId: existingPaymentMethodId,
          }),
          errorCallback: (errorMessage) => {
            if (errorMessage) setInlineError(errorMessage);
            setSubmitting(false);
          },
        });
      } else {
        setInlineError("Invalid Verification Code");
        setSubmitting(false);
      }
    }
  };

  const onSendVerificationCode = async () => {
    if (resendCooldownSeconds > 0) return;

    setInlineError(null);
    setSending(true);
    try {
      await bookingApi.sendVerificationCode(
        phoneNumber,
        appointment,
        routeState.verificationPurpose,
      );
      setResendAvailableAt(
        Date.now() + VERIFICATION_RESEND_COOLDOWN_SECONDS * 1000,
      );
      messageApi.success("Sent");
    } catch (err: any) {
      setInlineError(extractErrorMessage(err));
    }
    setSending(false);
  };

  if (showInstructure) {
    return <TroubleShootingForCode onClose={() => setShowInstructure(false)} />;
  }
  return (
    <>
      {contextHolder}
      <Typography.Paragraph>
        We&#39;ve sent a verification code to your mobile number. Please fill
        out the form with the code to continue.
      </Typography.Paragraph>

      <Space orientation="vertical" style={{ width: "100%" }}>
        <Form layout="vertical">
          {!isVerifyingUpcomingAppointments && (
            <>
              <Form.Item
                label={<Text strong>First Name</Text>}
                htmlFor="firstName"
              >
                <Input
                  id="firstName"
                  placeholder="First Name"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                  }}
                />
              </Form.Item>
              <Form.Item
                label={<Text strong>Last Name</Text>}
                htmlFor="lastName"
              >
                <Input
                  id="lastName"
                  placeholder="Last Name"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                  }}
                />
              </Form.Item>
            </>
          )}
          <Form.Item
            label={<Text strong>Verification Code</Text>}
            htmlFor="code"
          >
            {/* Mark the field as an SMS code so iOS/macOS Safari and Android
                Chrome offer the code from Messages instead of guessing. No
                maxLength: Twilio Verify sets the code length, not this app. */}
            <Input
              id="code"
              placeholder="Code"
              autoComplete="one-time-code"
              inputMode="numeric"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
              }}
            />
          </Form.Item>
        </Form>

        {/* <div className="has-text-right">
          <Typography.Link
            type="secondary"
            onClick={() => setShowInstructure(true)}
          >
            <small>Having trouble receiving the code?</small>
          </Typography.Link>
        </div> */}
      </Space>
      {inlineError && (
        <Alert
          type="error"
          showIcon
          message={inlineError}
          style={{ marginTop: 16 }}
        />
      )}
      <Divider dashed />
      <Flex justify="space-between" align="center">
        <Button
          type="primary"
          loading={isSubmitting}
          disabled={
            isSubmitting ||
            !code?.trim() ||
            (!isVerifyingUpcomingAppointments &&
              (!firstName?.trim() || !lastName?.trim()))
          }
          onClick={handleSubmit}
        >
          Submit
        </Button>
        <Button
          type="link"
          disabled={isSending || isSubmitting || resendCooldownSeconds > 0}
          onClick={onSendVerificationCode}
        >
          {resendCooldownSeconds > 0
            ? `Re-send in ${resendCooldownSeconds}s`
            : "Re-send"}
        </Button>
      </Flex>
    </>
  );
};

export default FirstClient;
